<script setup>
  import { computed } from "vue";
  import { Label } from "@/components/ui/label";
  import { Switch } from "@/components/ui/switch";
  import { Select } from "@/components/episto-ui/select";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Checkbox } from "@/components/ui/checkbox";

  const props = defineProps({
    fieldName: {
      type: String,
      required: true,
    },
    cardToken: {
      type: String,
      default: null,
    },
    isMatrixQuestion: {
      type: Boolean,
      default: false,
    },
    hasCrosstab: {
      type: Boolean,
      default: false,
    },
    crosstabType: {
      type: String,
      default: "question",
    },
    crosstabQuestionId: {
      type: [Number, String],
      default: null,
    },
    crosstabGroupTokens: {
      type: Array,
      default: () => [],
    },
    crosstabQuestionOptions: {
      type: Array,
      default: () => [],
    },
    groupOptions: {
      type: Array,
      default: () => [],
    },
    hasReportDefault: {
      type: Boolean,
      default: false,
    },
  });

  const crosstabQuestionSelectOptions = computed(() =>
    props.crosstabQuestionOptions.map((q) => ({
      value: q.value.toString(),
      label: q.label,
    })),
  );

  const emit = defineEmits([
    "update:hasCrosstab",
    "update:crosstabType",
    "update:crosstabQuestionId",
    "update:crosstabGroupTokens",
  ]);

  function toggleGroup(groupToken) {
    const currentTokens = props.crosstabGroupTokens;
    let newTokens;
    if (currentTokens.includes(groupToken)) {
      newTokens = currentTokens.filter((t) => t !== groupToken);
    } else {
      newTokens = [...currentTokens, groupToken];
    }
    emit("update:crosstabGroupTokens", newTokens);
  }
</script>

<template>
  <div v-if="cardToken && !isMatrixQuestion">
    <div class="flex items-center gap-2">
      <Switch
        :model-value="hasCrosstab"
        @update:model-value="$emit('update:hasCrosstab', $event)" />
      <Label class="text-sm">{{ hasReportDefault ? "Override report-level cross-tabulation" : "Cross-tabulation" }}</Label>
    </div>

    <div
      v-if="hasCrosstab"
      class="mt-3 space-y-3">
      <!-- Crosstab type selection -->
      <div class="flex gap-4">
        <label class="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            :name="`crosstab-type-${fieldName}`"
            value="question"
            :checked="crosstabType === 'question'"
            class="text-primary"
            @change="$emit('update:crosstabType', 'question')" />
          Question
        </label>
        <label class="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            :name="`crosstab-type-${fieldName}`"
            value="groups"
            :checked="crosstabType === 'groups'"
            class="text-primary"
            @change="$emit('update:crosstabType', 'groups')" />
          Saved groups
        </label>
      </div>

      <!-- Crosstab question selector -->
      <div
        v-if="crosstabType === 'question'"
        class="w-1/2 min-w-0">
        <Select
          :model-value="crosstabQuestionId?.toString() || undefined"
          :options="crosstabQuestionSelectOptions"
          placeholder="Select a question"
          @update:model-value="$emit('update:crosstabQuestionId', $event ? parseInt($event) : null)" />
      </div>

      <!-- Crosstab groups selector -->
      <div
        v-if="crosstabType === 'groups'"
        class="w-1/2 min-w-0">
        <Popover>
          <PopoverTrigger as-child>
            <button
              type="button"
              class="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <span :class="crosstabGroupTokens.length === 0 ? 'text-muted-foreground' : ''">
                {{
                  crosstabGroupTokens.length === 0
                    ? "Select groups..."
                    : `${crosstabGroupTokens.length} group(s) selected`
                }}
              </span>
              <svg
                class="h-4 w-4 opacity-50"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </PopoverTrigger>
          <PopoverContent
            class="w-[200px] p-2"
            align="start">
            <div class="space-y-1 max-h-[200px] overflow-y-auto">
              <div
                v-for="g in groupOptions"
                :key="g.value"
                class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer"
                @click.prevent.stop="toggleGroup(g.value)">
                <Checkbox
                  :model-value="crosstabGroupTokens.includes(g.value)"
                  class="pointer-events-none" />
                <span class="text-sm">{{ g.label }}</span>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  </div>
</template>
